import React, { useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { useParams, Link, useHistory } from 'react-router-dom'
import { Modal } from 'react-bootstrap'
import {
    Button,
    Container,
    Icon,
    Message,
    Segment,
    List,
    Form,
    Header,
    Transition,
    Divider,
} from 'semantic-ui-react'
import moment from 'moment'
import {
    DELETE_JOB_MUTATION,
    JOB_BASIC,
    UPDATE_JOB_MUTATION,
} from '../../../graphql/job'
import { LOAD_MANAGE_JOBS_QUERY } from '../../../graphql/admin'
import LoadingSpinner from '../../../components/LoadingSpinner'

const EditForm = ({ job }) => {
    const [isSaved, setIsSaved] = useState(false)
    const [jobStatus, setJobStatus] = useState(job.overall_status)
    const [offerRate, setOfferRate] = useState(job.offerRate ?? '')
    const [adminNotes, setAdminNotes] = useState(job.adminNotes || '')
    const [updateJob, { loading: updating }] = useMutation(
        UPDATE_JOB_MUTATION,
        {
            onCompleted: () => {
                setIsSaved(true)
                setTimeout(() => setIsSaved(false), 3000)
            },
        }
    )
    const onSubmit = () => {
        const jobInput = { adminNotes }
        if (offerRate) {
            jobInput.hourlyRate = offerRate
        }
        if (jobStatus) {
            jobInput.overall_status = jobStatus
        }
        if (jobStatus === 'Pending Scheduling') {
            jobInput.response_status = 'Pending'
            jobInput.declineReason = ''
            jobInput.qualificationResponses = []
        }
        console.log(job.user.id, job.project.id, jobInput)
        updateJob({
            variables: {
                userId: job.user.id,
                projectId: job.project.id,
                job: jobInput,
            },
        })
    }
    return (
        <Form onSubmit={onSubmit}>
            <Form.TextArea
                label="Admin note"
                value={adminNotes}
                onChange={(e, { value }) => setAdminNotes(value)}
            />
            <Divider />
            <Header>
                Change job status
            </Header>
            <Form.Dropdown
                label="Job status"
                selection
                value={jobStatus}
                options={[
                    {
                        key: 'pending scheduling',
                        text: 'Pending Scheduling',
                        value: 'Pending Scheduling',
                    },
                    { key: 'scheduled', text: 'Scheduled', value: 'Scheduled' },
                    { key: 'client reschedule', text: 'Client Reschedule', value: 'Client Reschedule' },
                    {
                        key: 'completed',
                        text: 'Completed',
                        value: 'Completed',
                    },
                    { key: 'cancelled', text: 'Cancelled', value: 'Cancelled' },
                    { key: 'paid', text: 'Paid', value: 'Paid' },
                    { key: 'expired', text: 'Expired', value: 'Expired' },
                    { key: 'declined', text: 'Declined', value: 'Declined' },
                    { key: 'no show', text: 'No Show', value: 'No Show' },
                    { key: 'expert no show', text: 'Expert No Show', value: 'Expert No Show' },
                    { key: 'client no show', text: 'Client No Show', value: 'Client No Show' },
                ]}
                onChange={(e, { value }) => setJobStatus(value)}
            />
            <span style={{ color: 'red' }}>
            Warning: This will directly modify the current job status in the database without triggering any action associated with status change. e.g. sending out emails, update zoom meetings, update transactions..etc
            </span>
            {job.offerRate && (
                <Form.Input
                    icon="dollar"
                    iconPosition="left"
                    type="number"
                    label="Offer Rate"
                    min={1}
                    value={offerRate}
                    onChange={(e, { value }) => setOfferRate(parseInt(value))}
                />
            )}
            <Divider />
            {isSaved && (
                <Transition visible={isSaved} animation="scale" duration={500}>
                    <Header color="green">Successfully saved!</Header>
                </Transition>
            )}
            <Form.Group>
                <Form.Button
                    primary
                    content="Save"
                    loading={updating}
                    disabled={updating}
                />
            </Form.Group>
        </Form>
    )
}

const DeleteJobSection = ({ job }) => {
    const history = useHistory()
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [deleteError, setDeleteError] = useState('')
    const canDelete = job.overall_status === 'Cancelled'
    const [deleteJob, { loading: deleting }] = useMutation(
        DELETE_JOB_MUTATION,
        {
            refetchQueries: [{ query: LOAD_MANAGE_JOBS_QUERY }],
            onCompleted: () => {
                alert('Job deleted successfully!')
                history.push('/admin/jobs')
            },
            onError: (error) => {
                setDeleteError(error.message)
            },
        }
    )

    const openDeleteModal = () => {
        setDeleteError('')
        if (canDelete) setShowDeleteModal(true)
    }

    const confirmDelete = () => {
        if (!canDelete) {
            setDeleteError('Only cancelled jobs can be deleted.')
            return
        }
        deleteJob({ variables: { id: job.id } })
    }

    return (
        <>
            <Divider />
            <Header as="h3" color="red">
                <Icon name="trash alternate outline" />
                <Header.Content>Delete job</Header.Content>
            </Header>
            <Message
                warning={!canDelete}
                negative={canDelete}
                icon={canDelete ? 'exclamation triangle' : 'lock'}
                header={
                    canDelete
                        ? 'This cancelled job can be deleted'
                        : 'Only cancelled jobs can be deleted'
                }
                content={
                    canDelete
                        ? 'Deleting this job is permanent. Please make sure you no longer need any information or record related to this job before deleting.'
                        : 'Please cancel this job on Client app before deleting this job.'
                }
            />
            {deleteError && <Message negative content={deleteError} />}
            <Button
                negative
                icon
                labelPosition="left"
                type="button"
                disabled={!canDelete || deleting}
                loading={deleting}
                onClick={openDeleteModal}
            >
                <Icon name="trash alternate" />
                Delete this job
            </Button>
            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Delete this job?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        This will permanently delete the cancelled job for{' '}
                        <strong>{job.user.fullName}</strong> on{' '}
                        <strong>{job.project.title}</strong>.
                    </p>
                    {deleteError && <Message negative content={deleteError} />}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        type="button"
                        disabled={deleting}
                        onClick={() => setShowDeleteModal(false)}
                    >
                        Keep job
                    </Button>
                    <Button
                        negative
                        type="button"
                        loading={deleting}
                        disabled={deleting || !canDelete}
                        onClick={confirmDelete}
                    >
                        Delete job
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

const ManageJob = () => {
    const { id } = useParams()
    const { data, loading } = useQuery(JOB_BASIC, {
        variables: { id },
    })
    if (loading) return <LoadingSpinner />

    const { job } = data

    const INFORMATION = [
        {
            name: 'Job status',
            value: job.overall_status,
        },
        {
            name: 'Team',
            value: job.project.team.name,
        },
        {
            name: 'Project title',
            value: job.project.title,
        },
        {
            name: 'Project type',
            value: job.project.type,
        },
        {
            name: 'Project information',
            value: (
                <Link to={`/admin/projects/${job.project.id}`}>
                    Click to view project page
                </Link>
            ),
        },
        {
            name: 'Meeting time',
            value: job.meetingAt ? moment(job.meetingAt).format('LLL') : null,
        },
        {
            name: 'Meeting link',
            value: job.zoomMeeting.meetingLink,
        },
        {
            name: 'Zoom link',
            value: (
                <a
                    href={`https://us06web.zoom.us/user/${job.zoomMeeting.zoomUserId}/meeting/${job.zoomMeeting.zoomId}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    Click here to access zoom page
                </a>
            ),
        },
        {
            name: 'Expert name',
            value: `${job.user.fullName} (${job.user.email})`,
        },
        {
            name: 'Expert country',
            value: job.expert.country,
        },
        {
            name: 'Expert stripe status',
            value: job.expert.stripeAccount.isConnected
                ? 'Connected'
                : 'Not connected',
        },
        {
            name: 'Payment method',
            value: job.payment?.method,
        },
        {
            name: 'Payment paid at',
            value: job.payment?.createdAt
                ? moment(job.payment.createdAt).format('LLL')
                : null,
        },
        {
            name: 'Payment amount',
            value: job.payment?.amount,
        },
        {
            name: 'Payment description',
            value: job.payment?.description,
        },
    ]

    return (
        <Container>
            <Segment>
                <List>
                    {INFORMATION.filter((info) => info.value !== null).map(
                        (info) => (
                            <List.Item key={info.name}>
                                <List.Content>
                                    <List.Header>{info.name}</List.Header>
                                    <List.Description>
                                        {info.value}
                                    </List.Description>
                                </List.Content>
                            </List.Item>
                        )
                    )}
                    {job.bonuses?.length > 0 && (
                        <List.Item>
                            <List.Content>
                                <List.Header>Bonuses information</List.Header>
                                <List.Description>
                                    <List bulleted>
                                        {job.bonuses.map((bonus) => (
                                            <List.Item key={bonus.createdAt}>
                                                {`${moment(
                                                    bonus.createdAt
                                                ).format('LLL')} | ${
                                                    bonus.description
                                                }`}
                                            </List.Item>
                                        ))}
                                    </List>
                                </List.Description>
                            </List.Content>
                        </List.Item>
                    )}
                </List>
                <EditForm job={job} />
                <DeleteJobSection job={job} />
            </Segment>
        </Container>
    )
}

export default ManageJob
